import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, Package, PencilSimple, Warning, ArrowsClockwise, BoxArrowDown, ListChecks, X } from "@phosphor-icons/react";
import { ReceiveBatchDialog } from "@/components/admin/inventory/ReceiveBatchDialog";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";

const CATEGORIES = [
    { id: "resin",           label: "Resin",           prefix: "RES" },
    { id: "tpu_sheet",       label: "TPU sheet",       prefix: "TPU" },
    { id: "retainer_sheet",  label: "Retainer sheet",  prefix: "RET" },
    { id: "ipa",             label: "IPA",             prefix: "IPA" },
    { id: "consumables",     label: "Consumables",     prefix: "CON" },
    { id: "packaging",       label: "Packaging",       prefix: "PKG" },
    { id: "other",           label: "Other",           prefix: "MAT" },
];
const UNITS = ["units", "kg", "g", "l", "ml", "sheets", "bottles", "boxes", "rolls"];

/* iter97.33 — Pack/Piece (UoM) story:
   • `unit`            — historical field. Treat as the consumption UoM (the
     piece-level unit a tech consumes: sheet/g/ml).
   • `consumption_uom` — explicit alias of `unit`. We always send it equal to
     `unit` from the form for clarity.
   • `purchase_uom`    — how it's BOUGHT (box/carton/roll/case). Blank means
     bought in the same UoM as consumed (1:1 — no pack).
   • `pack_size`       — pieces per pack. Stock is ALWAYS stored + reported in
     the consumption UoM; the pack view is computed on the fly. */
const PURCHASE_UOM_OPTIONS = ["", "box", "carton", "case", "pack", "roll", "bottle", "kit", "drum"];

const CAT_TONE = {
    resin:          "bg-amber-100 text-amber-800",
    tpu_sheet:      "bg-violet-100 text-violet-800",
    retainer_sheet: "bg-sky-100 text-sky-800",
    ipa:            "bg-emerald-100 text-emerald-800",
    consumables:    "bg-slate-100 text-slate-700",
    packaging:      "bg-rose-100 text-rose-700",
    other:          "bg-secondary text-muted-foreground",
};

const EMPTY = {
    name: "",
    sku: "",
    category: "resin",
    unit: "units",
    reorder_threshold: 0,
    unit_cost_estimate: 0,
    description: "",
    // iter97.33 — pack/piece fields
    purchase_uom: "",
    pack_size: 1,
    consumption_uom: "",
};


/* iter97.33 — Format helpers.
   `formatStock` — given a material doc, returns { base: "242 sheets",
   packs: "4.84 boxes" } when pack_size > 1, otherwise packs is null. Used
   everywhere stock is shown so admins always see both views at once. */
function pluralizeUom(uom, count) {
    if (!uom) return "";
    if (count === 1) return uom;
    // Smart English pluralize for common pack nouns
    const lower = uom.toLowerCase();
    if (/(s|x|ch|sh)$/.test(lower)) return `${uom}es`;     // box → boxes, bus → buses
    if (/[^aeiou]y$/.test(lower))   return `${uom.slice(0, -1)}ies`; // category → categories
    return `${uom}s`;
}

function formatStock(material, quantity) {
    const qty = Number(quantity ?? material?.current_stock ?? 0);
    const unit = material?.unit || material?.consumption_uom || "";
    const packSize = Number(material?.pack_size || 1);
    const packUom = (material?.purchase_uom || "").trim();
    const base = `${qty.toLocaleString("en-IN", { maximumFractionDigits: 2 })} ${unit}`;
    if (packSize > 1 && packUom) {
        const packs = qty / packSize;
        const packsStr = (Math.round(packs * 100) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });
        return { base, packs: `${packsStr} ${pluralizeUom(packUom, packs)}` };
    }
    return { base, packs: null };
}


/* iter97.33 — Pack configuration sub-section of the Add/Edit Material
   dialog. When the admin says "I buy this in boxes of 50 sheets", the
   backend stores stock in sheets (the base) and the rest of the UI shows
   both views: "250 sheets (5 boxes)". */
function PackConfigSection({ form, setForm }) {
    const isPack = !!(form.purchase_uom && form.purchase_uom !== form.unit);
    const totalPerPack = Math.max(Number(form.pack_size) || 1, 1);
    return (
        <div className="col-span-2 border-t border-border pt-3 mt-1 space-y-2.5 bg-emerald-50/30 rounded-lg p-3" data-testid="material-pack-config">
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-900">
                    Pack configuration
                </div>
                <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                    Bought in a multi-pack? Tell us how many <span className="font-semibold">{form.unit || "pieces"}</span> are in one purchase pack — stock is always tracked in <span className="font-semibold">{form.unit || "pieces"}</span>.
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <Label className="text-[10px]">Purchase pack</Label>
                    <select
                        className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                        value={form.purchase_uom || ""}
                        onChange={(e) => setForm({ ...form, purchase_uom: e.target.value })}
                        data-testid="material-purchase-uom"
                    >
                        <option value="">— same as consumption —</option>
                        {PURCHASE_UOM_OPTIONS.filter((o) => o).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div>
                    <Label className="text-[10px]">Pack size</Label>
                    <Input
                        type="number"
                        min="1"
                        step="1"
                        value={form.pack_size}
                        disabled={!form.purchase_uom}
                        onChange={(e) => setForm({ ...form, pack_size: e.target.value })}
                        placeholder="50"
                        className="h-9"
                        data-testid="material-pack-size"
                    />
                </div>
                <div>
                    <Label className="text-[10px]">Pieces per pack</Label>
                    <div className="h-9 px-2 rounded-md border border-dashed border-input bg-card/40 text-sm flex items-center font-mono tabular-nums text-emerald-900">
                        {isPack ? `${totalPerPack} ${form.unit}` : "—  (1:1)"}
                    </div>
                </div>
            </div>
            {isPack && (
                <div className="text-[11px] text-emerald-900 leading-snug bg-emerald-100/50 rounded px-2.5 py-1.5 font-medium" data-testid="material-pack-preview">
                    Receiving <span className="font-bold">1 {form.purchase_uom}</span> = <span className="font-bold">{totalPerPack} {form.unit}</span> added to stock.
                </div>
            )}
        </div>
    );
}


export function MaterialsTab({ onChange }) {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [chip, setChip] = useState("all"); // all | low | healthy
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [adjustFor, setAdjustFor] = useState(null);
    const [receiveBatchFor, setReceiveBatchFor] = useState(null);
    const [variantsFor, setVariantsFor] = useState(null);
    const [categoryFieldDefs, setCategoryFieldDefs] = useState([]); // fields for the current form.category
    const [skuTouched, setSkuTouched] = useState(false); // true once the admin manually edits the SKU

    const load = () => api.get("/admin/inventory/materials").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    // Fetch the per-category extra-field definitions whenever the form's
    // selected category changes — drives dynamic input rendering.
    useEffect(() => {
        if (!open || !form.category) { setCategoryFieldDefs([]); return; }
        api.get(`/admin/inventory/field-definitions/${form.category}`)
            .then((r) => setCategoryFieldDefs(r.data?.fields || []))
            .catch(() => setCategoryFieldDefs([]));
    }, [open, form.category]);

    /* iter97.33 — Auto-fill SKU from the chosen category. Runs whenever the
       category changes AND the admin hasn't manually typed in the SKU field
       yet (so we never overwrite a user-entered value). On edit, we skip
       auto-fill — existing SKUs are preserved. */
    useEffect(() => {
        if (!open || editing || skuTouched) return;
        if (!form.category) return;
        api.get(`/admin/inventory/sku-suggest?category=${form.category}`)
            .then((r) => {
                const next = r.data?.next_sku;
                if (next) setForm((f) => ({ ...f, sku: next }));
            })
            .catch(() => { /* non-fatal — admin can type their own SKU */ });
    }, [open, editing, form.category, skuTouched]);

    const stats = useMemo(() => {
        const s = { total: items.length, low: 0, healthy: 0 };
        items.forEach((m) => {
            const isLow = (m.reorder_threshold || 0) > 0 && (m.current_stock || 0) <= m.reorder_threshold;
            if (isLow) s.low += 1; else s.healthy += 1;
        });
        return s;
    }, [items]);

    const filtered = useMemo(() => {
        const ql = q.trim().toLowerCase();
        return items.filter((m) => {
            const isLow = (m.reorder_threshold || 0) > 0 && (m.current_stock || 0) <= m.reorder_threshold;
            if (chip === "low" && !isLow) return false;
            if (chip === "healthy" && isLow) return false;
            if (!ql) return true;
            return (m.name || "").toLowerCase().includes(ql)
                || (m.sku || "").toLowerCase().includes(ql)
                || (m.category || "").toLowerCase().includes(ql);
        });
    }, [items, q, chip]);

    const openCreate = () => { setEditing(null); setSkuTouched(false); setForm({ ...EMPTY, custom_fields: {} }); setOpen(true); };
    const openEdit = (m) => { setEditing(m); setSkuTouched(true); setForm({ ...EMPTY, ...m, custom_fields: m.custom_fields || {} }); setOpen(true); };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        try {
            const payload = {
                ...form,
                /* SKU is optional — backend auto-generates when blank */
                sku: (form.sku || "").trim(),
                reorder_threshold: Number(form.reorder_threshold) || 0,
                unit_cost_estimate: Number(form.unit_cost_estimate) || 0,
                /* iter97.33 — pack/piece fields. consumption_uom defaults to
                   the `unit` field; purchase_uom blank means no pack. */
                purchase_uom: (form.purchase_uom || "").trim(),
                pack_size: Math.max(Number(form.pack_size) || 1, 1),
                consumption_uom: (form.consumption_uom || form.unit || "").trim(),
                custom_fields: form.custom_fields || {},
            };
            if (editing) {
                await api.put(`/admin/inventory/materials/${editing.id}`, payload);
                toast.success("Material updated");
            } else {
                const r = await api.post("/admin/inventory/materials", payload);
                toast.success(`Material added — ${r.data?.sku || ""}`);
            }
            setOpen(false); load(); onChange?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const setCustom = (key, val) => setForm((f) => ({ ...f, custom_fields: { ...(f.custom_fields || {}), [key]: val } }));

    return (
        <div className="space-y-4" data-testid="materials-tab">
            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search by name, SKU, category…"
                searchTestid="materials-search"
                testid="materials-search-bar"
                rightSlot={(
                    <Button size="sm" className="rounded-full" onClick={openCreate} data-testid="materials-add">
                        <Plus size={14} weight="bold" className="mr-1.5" /> Add Material
                    </Button>
                )}
            />

            <FilterChipRow
                testid="materials-chip-row"
                chipTestidPrefix="materials-chip"
                value={chip}
                onChange={setChip}
                chips={[
                    { id: "all",     label: "All",         count: stats.total,   tone: "primary" },
                    { id: "low",     label: "Low stock",   count: stats.low,     tone: "rose" },
                    { id: "healthy", label: "Healthy",     count: stats.healthy, tone: "emerald" },
                ]}
            />

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
                <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                    <div className="col-span-3">SKU / Name</div>
                    <div className="col-span-2">Category</div>
                    <div className="col-span-2 text-right">Stock</div>
                    <div className="col-span-2 text-right">Reorder ≤</div>
                    <div className="col-span-2 text-right">Unit cost (est)</div>
                    <div className="col-span-1 text-right">Actions</div>
                </div>
                {filtered.length === 0 ? (
                    <div className="py-10 text-center text-muted-foreground text-sm" data-testid="materials-empty">
                        {items.length === 0 ? "No materials yet — start by adding one (e.g. Resin Type A)." : "No materials match the search."}
                    </div>
                ) : filtered.map((m) => {
                    const isLow = (m.reorder_threshold || 0) > 0 && (m.current_stock || 0) <= m.reorder_threshold;
                    const stockDisplay = formatStock(m); // iter97.33 — pack-aware
                    return (
                        <div key={m.id} className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 border-b border-border last:border-0 items-center ${isLow ? "bg-rose-50/30" : ""}`} data-testid={`material-row-${m.id}`}>
                            <div className="col-span-3 min-w-0">
                                <div className="font-mono text-[11px] font-bold uppercase text-muted-foreground">{m.sku}</div>
                                <div className="font-semibold text-sm truncate">{m.name}</div>
                            </div>
                            <div className="col-span-2">
                                <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded ${CAT_TONE[m.category] || CAT_TONE.other}`}>
                                    {CATEGORIES.find((c) => c.id === m.category)?.label || m.category}
                                </span>
                            </div>
                            <div className="col-span-2 text-right">
                                <div className={`font-bold tabular-nums ${isLow ? "text-rose-700" : ""}`}>
                                    {stockDisplay.base}
                                    {isLow && <Warning size={12} weight="fill" className="text-rose-600 inline ml-1 -mt-0.5" />}
                                </div>
                                {stockDisplay.packs && (
                                    <div className="text-[10px] text-muted-foreground tabular-nums" data-testid={`material-stock-packs-${m.id}`}>
                                        ≈ {stockDisplay.packs}
                                    </div>
                                )}
                            </div>
                            <div className="col-span-2 text-right tabular-nums text-sm text-muted-foreground">{m.reorder_threshold || "—"}</div>
                            <div className="col-span-2 text-right tabular-nums text-sm text-muted-foreground">{m.unit_cost_estimate ? `₹${m.unit_cost_estimate}` : "—"}</div>
                            <div className="col-span-1 text-right flex items-center justify-end gap-1">
                                <button type="button" onClick={() => setReceiveBatchFor(m)} title="Receive batch" className="p-1 hover:bg-emerald-50 rounded" data-testid={`material-receive-batch-${m.id}`}>
                                    <BoxArrowDown size={13} className="text-emerald-700" />
                                </button>
                                <button type="button" onClick={() => setVariantsFor(m)} title="Manage size variants" className="p-1 hover:bg-violet-50 rounded relative" data-testid={`material-variants-${m.id}`}>
                                    <ListChecks size={13} className="text-violet-700" />
                                    {(m.variants?.length || 0) > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 text-[8px] font-bold bg-violet-600 text-white rounded-full w-3 h-3 flex items-center justify-center tabular-nums">
                                            {m.variants.length}
                                        </span>
                                    )}
                                </button>
                                <button type="button" onClick={() => setAdjustFor(m)} title="Adjust stock" className="p-1 hover:bg-secondary rounded" data-testid={`material-adjust-${m.id}`}>
                                    <ArrowsClockwise size={13} className="text-muted-foreground" />
                                </button>
                                <button type="button" onClick={() => openEdit(m)} title="Edit" className="p-1 hover:bg-secondary rounded" data-testid={`material-edit-${m.id}`}>
                                    <PencilSimple size={13} className="text-muted-foreground" />
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="material-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Material" : "Add Material"}</DialogTitle>
                        <DialogDescription>
                            {editing
                                ? "SKUs must be unique — change carefully."
                                : "SKU auto-generates from the category. You can still override it if you have a preferred code."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label>Name</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Resin Type A, 1 kg" data-testid="material-name" />
                        </div>
                        <div>
                            <Label>Category</Label>
                            <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={form.category}
                                onChange={(e) => {
                                    /* iter97.33 — Changing the category resets
                                       the SKU lock so the auto-suggest effect
                                       re-fires and fills the new prefix. */
                                    setSkuTouched(false);
                                    setForm({ ...form, category: e.target.value });
                                }}
                                data-testid="material-category"
                            >
                                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="flex items-center justify-between">
                                <span>SKU</span>
                                {!editing && !skuTouched && (
                                    <span className="text-[10px] font-normal text-emerald-700">auto · click to edit</span>
                                )}
                            </Label>
                            <Input
                                value={form.sku}
                                onChange={(e) => { setSkuTouched(true); setForm({ ...form, sku: e.target.value.toUpperCase() }); }}
                                placeholder={`${(CATEGORIES.find((c) => c.id === form.category)?.prefix) || "MAT"}-0001`}
                                className={`font-mono ${!editing && !skuTouched ? "text-muted-foreground" : ""}`}
                                data-testid="material-sku"
                            />
                        </div>
                        <div>
                            <Label>Consumption unit · how it&apos;s used in production</Label>
                            <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={form.unit}
                                onChange={(e) => setForm({ ...form, unit: e.target.value, consumption_uom: e.target.value })}
                                data-testid="material-unit"
                            >
                                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Reorder threshold (in {form.unit})</Label>
                            <Input type="number" value={form.reorder_threshold} onChange={(e) => setForm({ ...form, reorder_threshold: e.target.value })} placeholder="0" data-testid="material-reorder" />
                        </div>
                        <div>
                            <Label>Unit cost estimate (₹ per {form.unit})</Label>
                            <Input type="number" value={form.unit_cost_estimate} onChange={(e) => setForm({ ...form, unit_cost_estimate: e.target.value })} placeholder="0" data-testid="material-unit-cost" />
                        </div>
                        <div className="col-span-2">
                            <Label>Description (optional)</Label>
                            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="material-desc" />
                        </div>

                        {/* iter97.33 — Pack configuration. Captures how the
                            material is BOUGHT (purchase_uom + pack_size) vs.
                            how it's CONSUMED (the `unit` field above).
                            Backend stores stock in the consumption unit; the
                            pack view is computed on the fly. */}
                        <PackConfigSection form={form} setForm={setForm} />

                        {categoryFieldDefs.length > 0 && (
                            <div className="col-span-2 border-t border-border pt-3 mt-1 space-y-3" data-testid="material-custom-fields">
                                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                    Extra fields · {CATEGORIES.find((c) => c.id === form.category)?.label || form.category}
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {categoryFieldDefs.map((d) => {
                                        const val = (form.custom_fields || {})[d.key] ?? "";
                                        return (
                                            <div key={d.key} className={d.type === "select" || d.type === "text" || d.type === "number" || d.type === "date" ? "" : "col-span-2"}>
                                                <Label>{d.label}{d.required && <span className="text-rose-600 ml-0.5">*</span>}</Label>
                                                {d.type === "select" ? (
                                                    <select
                                                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                                        value={val}
                                                        onChange={(e) => setCustom(d.key, e.target.value)}
                                                        data-testid={`material-custom-${d.key}`}
                                                    >
                                                        <option value="">— Select —</option>
                                                        {(d.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                                    </select>
                                                ) : (
                                                    <Input
                                                        type={d.type === "number" ? "number" : (d.type === "date" ? "date" : "text")}
                                                        value={val}
                                                        onChange={(e) => setCustom(d.key, e.target.value)}
                                                        data-testid={`material-custom-${d.key}`}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="material-save">{editing ? "Save" : "Add"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AdjustDialog material={adjustFor} onClose={() => setAdjustFor(null)} onSaved={() => { load(); onChange?.(); }} />
            <ReceiveBatchDialog
                material={receiveBatchFor}
                open={!!receiveBatchFor}
                onClose={() => setReceiveBatchFor(null)}
                onSaved={() => { setReceiveBatchFor(null); load(); onChange?.(); }}
            />
            <VariantsDialog
                material={variantsFor}
                onClose={() => setVariantsFor(null)}
                onSaved={() => { load(); onChange?.(); }}
            />
        </div>
    );
}


function AdjustDialog({ material, onClose, onSaved }) {
    const [delta, setDelta] = useState("");
    const [reason, setReason] = useState("");
    const [type, setType] = useState("adjustment");

    useEffect(() => {
        if (material) { setDelta(""); setReason(""); setType("adjustment"); }
    }, [material]);

    if (!material) return null;
    const submit = async () => {
        const d = Number(delta);
        if (!Number.isFinite(d) || d === 0) { toast.error("Enter a non-zero quantity (use - for deductions)"); return; }
        if (reason.trim().length < 5) { toast.error("Reason must be at least 5 characters"); return; }
        try {
            await api.post("/admin/inventory/movements/adjust", { material_id: material.id, delta: d, reason: reason.trim(), movement_type: type });
            toast.success("Stock adjusted");
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };
    return (
        <Dialog open={!!material} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent data-testid="material-adjust-dialog">
                <DialogHeader>
                    <DialogTitle>Adjust stock — {material.sku}</DialogTitle>
                    <DialogDescription>Current: {material.current_stock || 0} {material.unit}. Use a negative number to deduct.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Movement type</Label>
                            <select className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" value={type} onChange={(e) => setType(e.target.value)} data-testid="adjust-type">
                                <option value="adjustment">Adjustment (count correction)</option>
                                <option value="wastage">Wastage / breakage</option>
                                <option value="return">Return to vendor</option>
                            </select>
                        </div>
                        <div>
                            <Label>Delta (in {material.unit})</Label>
                            <Input type="number" value={delta} onChange={(e) => setDelta(e.target.value)} placeholder="e.g. -2.5 or 10" data-testid="adjust-delta" />
                        </div>
                    </div>
                    <div>
                        <Label>Reason (≥ 5 chars)</Label>
                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Quarterly count showed 2 fewer bottles than ledger." data-testid="adjust-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} data-testid="adjust-save">Adjust</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* iter97.20 — VariantsDialog: per-material size/dimension list editor.
 *
 * Variants are a small fixed list per SKU (e.g. TPU sheet → A4 / A5 /
 * Custom-cut, Retainer sheet → Upper-only / Lower-only / Both arches).
 * Production tech picks one at log time. They live on the material doc
 * itself (since they're descriptive, not separately tracked).
 *
 * Full-set semantics on save — the entire array is replaced server-side.
 */
function VariantsDialog({ material, onClose, onSaved }) {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!material) return;
        setRows((material.variants || []).map((v) => ({ ...v })));
    }, [material]);

    if (!material) return null;

    const add = () => setRows((r) => [...r, { id: `new_${Math.random().toString(36).slice(2, 9)}`, label: "", sku_suffix: "", description: "" }]);
    const update = (idx, patch) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    const remove = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

    const save = async () => {
        const cleaned = rows
            .map((r) => ({
                id: r.id?.startsWith("new_") ? undefined : r.id,
                label: (r.label || "").trim(),
                sku_suffix: (r.sku_suffix || "").trim() || undefined,
                description: (r.description || "").trim() || undefined,
            }))
            .filter((r) => r.label);
        setBusy(true);
        try {
            await api.put(`/admin/inventory/materials/${material.id}/variants`, { variants: cleaned });
            toast.success(`Saved ${cleaned.length} variant(s)`);
            onSaved?.();
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save variants");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!material} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-2xl" data-testid="material-variants-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ListChecks size={16} weight="duotone" /> Variants — {material.sku}
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Size / dimension options the production tech will pick from when logging usage.
                        E.g. for TPU sheets: A4, A5, Custom-cut. Leave empty if this material has no variants.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                    {rows.length === 0 && (
                        <div className="text-xs text-muted-foreground italic py-4 text-center border border-dashed border-border rounded-xl">
                            No variants yet — click &ldquo;+ Add variant&rdquo; below.
                        </div>
                    )}
                    {rows.map((row, idx) => (
                        <div key={row.id || idx} className="grid grid-cols-12 gap-2 items-start p-2 rounded-lg border border-border bg-secondary/20" data-testid={`material-variant-row-${idx}`}>
                            <div className="col-span-5">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Label</Label>
                                <Input
                                    value={row.label || ""}
                                    onChange={(e) => update(idx, { label: e.target.value })}
                                    placeholder="A4 sheet"
                                    className="h-9 text-sm"
                                    data-testid={`material-variant-label-${idx}`}
                                />
                            </div>
                            <div className="col-span-3">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">SKU suffix (opt)</Label>
                                <Input
                                    value={row.sku_suffix || ""}
                                    onChange={(e) => update(idx, { sku_suffix: e.target.value.toUpperCase() })}
                                    placeholder="-A4"
                                    className="h-9 text-sm font-mono"
                                    data-testid={`material-variant-suffix-${idx}`}
                                />
                            </div>
                            <div className="col-span-3">
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Description (opt)</Label>
                                <Input
                                    value={row.description || ""}
                                    onChange={(e) => update(idx, { description: e.target.value })}
                                    placeholder="210x297mm"
                                    className="h-9 text-sm"
                                    data-testid={`material-variant-desc-${idx}`}
                                />
                            </div>
                            <div className="col-span-1 flex items-end justify-end h-full pb-0.5">
                                <button
                                    type="button"
                                    onClick={() => remove(idx)}
                                    className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                                    title="Remove"
                                    data-testid={`material-variant-remove-${idx}`}
                                >
                                    <X size={13} weight="bold" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    onClick={add}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                    data-testid="material-variant-add"
                >
                    <Plus size={12} weight="bold" /> Add variant
                </button>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={save} disabled={busy} data-testid="material-variants-save">
                        {busy ? "Saving…" : "Save variants"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
